import { useApolloClient, useMutation } from '@apollo/client';
import { Button, Grid, Icon, Typography } from '@mui/material';

import i18next, { t } from 'i18next';
import { useDispatch } from 'react-redux';

import START_LOTTERY_MUTATION from 'gql/mutations/ModuleSelectionLotteryService/startLottery.gql';
import LATEST_LOTTERY_QUERY from 'gql/queries/ModuleSelectionsService/getLatestLotteryBySelection.gql';
import USER_QUERY from 'gql/queries/currentUser.gql';

import { Lottery } from 'app/containers/AWPM-FWPM/StudentView/models';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { ACLHelper } from 'utils/ACLHelper';

import { ModuleSelection, State } from '../../models';

export const PreCalculationControl = ({
  moduleSelection,
  latestLotteryData
}: {
  moduleSelection: ModuleSelection;
  latestLotteryData: Lottery;
}) => {
  const dispatch = useDispatch();
  const [startLottery] = useMutation(START_LOTTERY_MUTATION, {
    context: { clientName: 'federation' }
  });
  const client = useApolloClient();
  const { currentUser } = client.readQuery({ query: USER_QUERY });
  const aclHelper = new ACLHelper(currentUser);

  return (
    <Grid container justifyContent="space-between" paddingY={3} alignItems="center">
      {(aclHelper.isSuperUser() || aclHelper.isSelectionAdmin()) && (
        <>
          <Grid item xs>
            <Grid container alignItems="center" justifyContent="center">
              <Button
                variant="text"
                disabled={moduleSelection.state !== State.Started}
                onClick={() => {
                  startLottery({
                    variables: {
                      moduleSelectionId: Number(moduleSelection.id)
                    },
                    refetchQueries: [
                      {
                        query: LATEST_LOTTERY_QUERY,
                        variables: { moduleSelectionId: Number(moduleSelection.id) },
                        context: { clientName: 'federation' }
                      }
                    ]
                  })
                    .then(() => {
                      dispatch(
                        AlertActions.success(
                          t(translations.pages.moduleSelectionLottery.startSuccess())
                        )
                      );
                    })
                    .catch(e => {
                      dispatch(
                        AlertActions.error(
                          t(translations.pages.moduleSelectionLottery.startFailed())
                        )
                      );
                    });
                }}
              >
                <Grid container spacing={1} alignContent="center">
                  <Grid item>
                    <Icon className="fas fa-calculator" style={{ color: 'black' }} />
                  </Grid>
                  <Grid item>{t(translations.pages.moduleSelectionLottery.precalculate())}</Grid>
                </Grid>
              </Button>
            </Grid>
          </Grid>
          <Grid item xs={2}>
            {latestLotteryData?.state && (
              <Grid container alignItems="center" justifyContent="center">
                <Icon className="fas fa-heart" />
                <Typography paddingLeft={1}>
                  {t(translations.pages.moduleSelectionLottery.state(), {
                    state: latestLotteryData.state
                  })}
                </Typography>
              </Grid>
            )}
          </Grid>
        </>
      )}
      <Grid item xs={4}>
        {latestLotteryData?.nextPreCalculation &&
          latestLotteryData.nextPreCalculation < new Date() && (
            <Grid container alignItems="center" justifyContent="center">
              <Icon className="fas fa-clock" />
              <Typography paddingLeft={1}>
                {t(translations.pages.moduleSelectionLottery.nextScheduledUpdate(), {
                  date: new Date(latestLotteryData?.nextPreCalculation).toLocaleString(
                    i18next.language ?? i18next.languages[0] ?? 'de-DE',
                    {
                      hour: '2-digit',
                      minute: '2-digit',
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    }
                  )
                })}
              </Typography>
            </Grid>
          )}
      </Grid>
      <Grid item xs>
        {latestLotteryData?.createdAt &&
          ![State.Completed, State.Archived].includes(moduleSelection.state) && (
            <Grid container alignItems="center" justifyContent="center">
              <Icon className="fas fa-hourglass" />
              <Typography paddingLeft={1}>
                {t(translations.pages.moduleSelectionLottery.lastUpdate(), {
                  date: new Date(latestLotteryData?.createdAt).toLocaleString(
                    i18next.language ?? i18next.languages[0] ?? 'de-DE',
                    {
                      hour: '2-digit',
                      minute: '2-digit',
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    }
                  )
                })}
              </Typography>
            </Grid>
          )}
      </Grid>
    </Grid>
  );
};
